import { useEffect, useState } from "react";
import { useLang } from "@/contexts/LangContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CaretDown, CaretUp } from "@phosphor-icons/react";
import {
  EXAM_SECTIONS,
  PARACLINICAL_SECTIONS,
  RAPID_TESTS_DUAL,
  RAPID_TESTS_TEXT,
  buildLabRows,
} from "@/lib/consultationSchema";

/**
 * Extended consultation form (two tabs: Examen clinic + Investigații paraclinice).
 *
 * State shape:
 *   value = {
 *     sumar: "…",                              // Examen clinic → Sumar (top-level textarea)
 *     enabledExam: [ sectionId, ... ],
 *     exam: { [sectionId]: { [fieldKey]: v } },
 *     enabledParaclinical: [ sectionId, ... ],
 *     paraclinical: { [sectionId]: { rows: [...] | fields... } },
 *   }
 */
export default function ExtendedConsultationTabs({ value, onChange, refItems = [] }) {
  const { t } = useLang();
  const v = value || {};
  const enabledExam = v.enabledExam || [];
  const enabledParaclinical = v.enabledParaclinical || [];

  function setBag(nextPartial) {
    onChange({ ...v, ...nextPartial });
  }

  function toggleExam(sectionId) {
    const next = enabledExam.includes(sectionId)
      ? enabledExam.filter((x) => x !== sectionId)
      : [...enabledExam, sectionId];
    setBag({ enabledExam: next });
  }

  function toggleParaclinical(sectionId, section) {
    if (enabledParaclinical.includes(sectionId)) {
      setBag({ enabledParaclinical: enabledParaclinical.filter((x) => x !== sectionId) });
      return;
    }
    // When enabling a lab section for the first time, pre-populate all
    // predefined parameters with their default unit/min/max so the vet only
    // needs to fill in Result. Data already present is preserved.
    const existing = (v.paraclinical || {})[sectionId] || {};
    let sectionValue = existing;
    if ((section.type === "lab" || section.type === "lab_with_obs") && !Array.isArray(existing.rows)) {
      sectionValue = { ...existing, rows: buildLabRows(section.category, refItems) };
    }
    setBag({
      enabledParaclinical: [...enabledParaclinical, sectionId],
      paraclinical: { ...(v.paraclinical || {}), [sectionId]: sectionValue },
    });
  }

  function setExamField(sectionId, fieldKey, val) {
    const bag = v.exam || {};
    const current = { ...(bag[sectionId] || {}), [fieldKey]: val };
    setBag({ exam: { ...bag, [sectionId]: current } });
  }

  function setParaclinicalField(sectionId, fieldKey, val) {
    const bag = v.paraclinical || {};
    const current = { ...(bag[sectionId] || {}), [fieldKey]: val };
    setBag({ paraclinical: { ...bag, [sectionId]: current } });
  }

  function setParaclinicalSection(sectionId, sectionValue) {
    setBag({ paraclinical: { ...(v.paraclinical || {}), [sectionId]: sectionValue } });
  }

  return (
    <Tabs defaultValue="exam">
      <TabsList className="bg-white border border-pf-border rounded-lg p-1 flex-wrap h-auto">
        <TabsTrigger value="exam" data-testid="ext-tab-exam">{t("examClinicTab")}</TabsTrigger>
        <TabsTrigger value="paraclinical" data-testid="ext-tab-paraclinical">{t("paraclinicalTab")}</TabsTrigger>
      </TabsList>

      {/* Examen clinic tab */}
      <TabsContent value="exam" className="mt-4 space-y-3">
        <div className="pf-card p-5">
          <div className="pf-section-title mb-3">{t("examClinicTab")}</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {EXAM_SECTIONS.map((section) => (
              <label
                key={section.id}
                data-testid={`exam-toggle-${section.id}`}
                className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                  enabledExam.includes(section.id)
                    ? "bg-pf-primary-light border-pf-primary/40"
                    : "bg-white border-pf-border hover:bg-pf-bg"
                }`}
              >
                <input
                  type="checkbox"
                  checked={enabledExam.includes(section.id)}
                  onChange={() => toggleExam(section.id)}
                  className="h-4 w-4 rounded border-pf-border text-pf-primary focus:ring-pf-primary"
                />
                <span className="text-xs sm:text-sm font-medium text-pf-text">{t(section.titleKey)}</span>
              </label>
            ))}
          </div>
        </div>

        {EXAM_SECTIONS.filter((s) => enabledExam.includes(s.id)).map((section) => (
          <div key={section.id} className="pf-card p-5" data-testid={`exam-section-${section.id}`}>
            <div className="pf-section-title mb-3">{t(section.titleKey)}</div>
            {section.id === "sumar" ? (
              <textarea
                data-testid="ext-sumar-input"
                rows={4}
                className="pf-input min-h-[110px]"
                value={v.sumar || ""}
                onChange={(e) => setBag({ sumar: e.target.value })}
                placeholder={t("examSumar")}
              />
            ) : (
              <GroupBody
                section={section}
                data={(v.exam || {})[section.id] || {}}
                setField={(k, val) => setExamField(section.id, k, val)}
                testIdPrefix={`ex-${section.id}`}
              />
            )}
          </div>
        ))}
      </TabsContent>

      {/* Investigații paraclinice tab */}
      <TabsContent value="paraclinical" className="mt-4 space-y-3">
        <div className="pf-card p-5">
          <div className="pf-section-title mb-3">{t("paraclinicalTab")}</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {PARACLINICAL_SECTIONS.map((section) => (
              <label
                key={section.id}
                data-testid={`par-toggle-${section.id}`}
                className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                  enabledParaclinical.includes(section.id)
                    ? "bg-pf-primary-light border-pf-primary/40"
                    : "bg-white border-pf-border hover:bg-pf-bg"
                }`}
              >
                <input
                  type="checkbox"
                  checked={enabledParaclinical.includes(section.id)}
                  onChange={() => toggleParaclinical(section.id, section)}
                  className="h-4 w-4 rounded border-pf-border text-pf-primary focus:ring-pf-primary"
                />
                <span className="text-xs sm:text-sm font-medium text-pf-text">{t(section.titleKey)}</span>
              </label>
            ))}
          </div>
        </div>

        {PARACLINICAL_SECTIONS.filter((s) => enabledParaclinical.includes(s.id)).map((section) => (
          <div key={section.id} className="pf-card p-5" data-testid={`par-section-${section.id}`}>
            <div className="pf-section-title mb-3">{t(section.titleKey)}</div>
            <ParaclinicalBody
              section={section}
              data={(v.paraclinical || {})[section.id] || {}}
              refItems={refItems}
              setField={(k, val) => setParaclinicalField(section.id, k, val)}
              setSection={(sv) => setParaclinicalSection(section.id, sv)}
              testIdPrefix={`par-${section.id}`}
            />
          </div>
        ))}
      </TabsContent>
    </Tabs>
  );
}

function SectionCard({ titleKey, enabled, onToggle, testIdPrefix, children }) {
  const { t } = useLang();
  const [open, setOpen] = useState(true);
  return (
    <div className="pf-card p-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={onToggle}
            data-testid={`${testIdPrefix}-toggle`}
            className="h-4 w-4 rounded border-pf-border text-pf-primary focus:ring-pf-primary"
          />
          <span className="pf-section-title">{t(titleKey)}</span>
        </label>
        {enabled && (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="p-1.5 rounded hover:bg-pf-bg text-pf-text-secondary"
          >
            {open ? <CaretUp size={14} /> : <CaretDown size={14} />}
          </button>
        )}
      </div>
      {enabled && open && <div className="mt-4">{children}</div>}
    </div>
  );
}

function ParaclinicalBody({ section, data, refItems, setField, setSection, testIdPrefix }) {
  // Auto-rebuild rows if user enables + refItems arrive later (defensive)
  useEffect(() => {
    if ((section.type === "lab" || section.type === "lab_with_obs")
        && !Array.isArray(data.rows)) {
      setSection({ ...data, rows: buildLabRows(section.category, refItems) });
    }
    // eslint-disable-next-line
  }, [section.id]);

  if (section.type === "lab") {
    return (
      <LabTablePreset
        rows={data.rows || buildLabRows(section.category, refItems)}
        onChange={(rows) => setSection({ ...data, rows })}
        testIdPrefix={testIdPrefix}
      />
    );
  }
  if (section.type === "lab_with_obs") {
    const { t } = { t: undefined };
    return (
      <div className="space-y-3">
        <LabTablePreset
          rows={data.rows || buildLabRows(section.category, refItems)}
          onChange={(rows) => setSection({ ...data, rows })}
          testIdPrefix={testIdPrefix}
        />
        <ObsField
          value={data[section.obsKey || "observatii"] || ""}
          onChange={(val) => setField(section.obsKey || "observatii", val)}
          testId={`${testIdPrefix}-obs`}
        />
      </div>
    );
  }
  if (section.type === "rapid_tests") {
    return <RapidTests sectionData={data} setField={setField} testIdPrefix={testIdPrefix} />;
  }
  return (
    <GroupBody
      section={section}
      data={data}
      setField={setField}
      testIdPrefix={testIdPrefix}
    />
  );
}

function ObsField({ value, onChange, testId }) {
  const { t } = useLang();
  return (
    <div>
      <label className="pf-label mb-1 block">{t("fObservatii")}</label>
      <textarea
        data-testid={testId}
        className="pf-input min-h-[70px]"
        rows={2}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function GroupBody({ section, data, setField, testIdPrefix }) {
  const { t } = useLang();
  return (
    <div className="space-y-4">
      {(section.groups || []).map((group, gIdx) => (
        <div key={gIdx}>
          {group.titleKey && (
            <div className="pf-label uppercase mb-2 text-pf-text">{t(group.titleKey)}</div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {group.fields.map((field) => (
              <FieldRenderer
                key={field.key}
                field={field}
                value={data[field.key]}
                onChange={(val) => setField(field.key, val)}
                testId={`${testIdPrefix}-${field.key}`}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function FieldRenderer({ field, value, onChange, testId }) {
  const { t } = useLang();
  const label = t(field.labelKey);
  if (field.type === "select") {
    return (
      <div>
        <label className="pf-label mb-1 block">{label}</label>
        <select
          data-testid={testId}
          className="pf-input"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">—</option>
          {(field.opts || []).map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      </div>
    );
  }
  if (field.type === "radio2") {
    return (
      <div>
        <label className="pf-label mb-1 block">{label}</label>
        <div className="flex gap-2">
          {(field.opts || []).map((opt) => {
            const active = value === opt;
            return (
              <button
                key={opt}
                type="button"
                onClick={() => onChange(active ? "" : opt)}
                data-testid={`${testId}-${opt}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all capitalize ${
                  active
                    ? "bg-pf-primary text-white border-pf-primary shadow-card"
                    : "bg-white border-pf-border text-pf-text hover:bg-pf-primary-light hover:border-pf-primary/40"
                }`}
              >
                {opt}
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  if (field.type === "textarea") {
    return (
      <div className="sm:col-span-2">
        <label className="pf-label mb-1 block">{label}</label>
        <textarea
          data-testid={testId}
          rows={2}
          className="pf-input min-h-[70px]"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    );
  }
  return (
    <div>
      <label className="pf-label mb-1 block">{label}</label>
      <input
        data-testid={testId}
        className="pf-input"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

/**
 * Pre-populated lab table. Rows are fixed (created when the category is
 * enabled). The user cannot add or remove rows — only edit the Result column
 * (and adjust unit / ref range if desired).
 */
function LabTablePreset({ rows, onChange, testIdPrefix }) {
  const { t } = useLang();

  function updateRow(idx, k, val) {
    onChange(rows.map((r, i) => (i === idx ? { ...r, [k]: val } : r)));
  }

  function highlightClass(row) {
    if (row.result === "" || row.result == null) return "";
    const v = parseFloat(String(row.result).replace(",", "."));
    if (Number.isNaN(v)) return "";
    if (row.ref_max != null && v > row.ref_max) return "lab-above";
    if (row.ref_min != null && v < row.ref_min) return "lab-below";
    return "";
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wider text-pf-text-secondary bg-pf-bg">
            <th className="px-3 py-2 rounded-l-lg w-[38%]">{t("parameter")}</th>
            <th className="px-3 py-2 w-[18%]">{t("result")}</th>
            <th className="px-3 py-2 w-[15%]">{t("unit")}</th>
            <th className="px-3 py-2 rounded-r-lg">{t("referenceRange")}</th>
          </tr>
        </thead>
        <tbody>
          {(rows || []).map((r, idx) => (
            <tr key={idx} className="border-b border-pf-border last:border-0">
              <td className="px-3 py-1.5 font-medium text-pf-text align-middle">{r.parameter}</td>
              <td className="px-2 py-1.5">
                <input
                  data-testid={`${testIdPrefix}-result-${idx}`}
                  className={`pf-input ${highlightClass(r)}`}
                  value={r.result || ""}
                  onChange={(e) => updateRow(idx, "result", e.target.value.replace(",", "."))}
                />
              </td>
              <td className="px-2 py-1.5">
                <input
                  className="pf-input"
                  value={r.unit || ""}
                  onChange={(e) => updateRow(idx, "unit", e.target.value)}
                />
              </td>
              <td className="px-2 py-1.5 whitespace-nowrap">
                <div className="flex items-center gap-1">
                  <input
                    className="pf-input !py-1.5 w-20"
                    placeholder="min"
                    value={r.ref_min ?? ""}
                    onChange={(e) => updateRow(idx, "ref_min", e.target.value === "" ? null : parseFloat(e.target.value))}
                  />
                  <span className="text-pf-text-secondary">–</span>
                  <input
                    className="pf-input !py-1.5 w-20"
                    placeholder="max"
                    value={r.ref_max ?? ""}
                    onChange={(e) => updateRow(idx, "ref_max", e.target.value === "" ? null : parseFloat(e.target.value))}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RapidTests({ sectionData, setField, testIdPrefix }) {
  const { t } = useLang();
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {RAPID_TESTS_DUAL.map((name) => {
          const val = sectionData[name] || {};
          return (
            <div key={name} className="p-3 rounded-lg border border-pf-border">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="pf-label mb-0 text-pf-text">{name}</span>
                <select
                  data-testid={`${testIdPrefix}-${name}-sel`}
                  className="pf-input !py-1 max-w-[140px]"
                  value={val.result || ""}
                  onChange={(e) => setField(name, { ...val, result: e.target.value })}
                >
                  <option value="">—</option>
                  <option value="Pozitiv">Pozitiv</option>
                  <option value="Negativ">Negativ</option>
                </select>
              </div>
              <label className="flex items-center gap-2 text-xs text-pf-text-secondary mt-2 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid={`${testIdPrefix}-${name}-obs-toggle`}
                  checked={!!val.obsEnabled}
                  onChange={(e) => setField(name, { ...val, obsEnabled: e.target.checked })}
                  className="h-3.5 w-3.5"
                />
                {t("fAdaugaObs")}
              </label>
              {val.obsEnabled && (
                <textarea
                  data-testid={`${testIdPrefix}-${name}-obs`}
                  rows={2}
                  className="pf-input mt-2 min-h-[60px]"
                  value={val.obs || ""}
                  onChange={(e) => setField(name, { ...val, obs: e.target.value })}
                />
              )}
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {RAPID_TESTS_TEXT.map((name) => (
          <div key={name}>
            <span className="pf-label mb-1 block">{name}</span>
            <input
              data-testid={`${testIdPrefix}-${name}-text`}
              className="pf-input"
              value={sectionData[name] || ""}
              onChange={(e) => setField(name, e.target.value)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
